import {getNodesWithSwapPotential, phoneNumberRegex, startWatching} from "./dom-watcher";
import { CallTrackingResponse, Swap, SwapItem, SwapResponse } from "../types";
import { initAscClickToCall } from "./asc-click-to-call";
import { CachedCallTrackingResponse } from "./repositories/api-swap-repository";
import {Api} from "./repositories/remote";
import {getMissingChanges} from "./renderers";

export type CallTrackingRepository = {
    current: {swaps: SwapItem[]}
    pending: Promise<CallTrackingResponse>,
}

export type PageContext = {
    url: string
    referrer: string
    title: string
}

export type PhoneNumberRenderer = (
    node: Node,
    swaps: SwapItem[],
) => void;


export type DniCache = {
    set<T>(key: string, data: T, ttl?: number): void;
    get<T>(key: string): T | null
    unset(key: string): void
    getExpiry(key: string): number | null
}

export type MonitorStorage = {
    extraNumbers?: string[],
    foundConflictingScript?: boolean,
    swapChanged?: boolean,
}

const cdns = [
    "invocacdn.com",
    "cdn.callrail.com",
    "revenue.io",
    "ksrndkehqnwntyxlhgto.com",
];

export default class App {
    private static instance: App;

    private constructor(
        private callTracking: CallTrackingRepository,
        private renderer: PhoneNumberRenderer,
        private undoPhoneNumberRenderer: VoidFunction,
        private storage: DniCache,
        private api: Api,
    ) {
        //
    }

    public static getInstance(
        callTracking: CallTrackingRepository,
        phoneNumberRenderer: PhoneNumberRenderer,
        undoPhoneNumberRenderer: VoidFunction,
        storage: DniCache,
        api: Api
    ): App {
        if (!App.instance) {
            App.instance = new App(callTracking, phoneNumberRenderer, undoPhoneNumberRenderer, storage, api);
        }

        return App.instance;
    }

    public run(): App {
        if (!this.storage.get<MonitorStorage>('800calltrk.monitors')) {
            this.storage.set('800calltrk.monitors', {
                extraNumbers: [],
                foundConflictingScript: false,
                swapChanged: false,
            })
        }

        const stop = startWatching(async (node) => {
            if (this.callTracking.current?.swaps) {
                this.renderer(node, this.callTracking.current.swaps);
            }
        });

        this.callTracking.pending.then((response) => {
            stop();

            this.undoPhoneNumberRenderer();

            if (response?.isSessionTrackingEnabled) {
                initAscClickToCall();
            }

            if (response?.swaps) {
                startWatching(async (node) => {
                    this.renderer(node, response.swaps);
                });
            }

            setTimeout(() => {
                const previouslyReported = this.storage.get<string[]>('800cltrk.extraNumbers') || [];

                const potentialSwaps = [
                    ...new Set(
                        getNodesWithSwapPotential(document.body)
                            .map(n => n.nodeValue?.match(phoneNumberRegex))
                            .filter(n => !!n)
                            .map(n => String(n).replace(/\D/g, ''))
                            .map(n => {
                                if (n.startsWith('1')) {
                                    n = "+" + n;
                                } else if (!n.startsWith("+")) {
                                    n = "+1" + n;
                                }

                                return n;
                            })
                    )
                ]
                    .filter((n) => !previouslyReported.includes(n));

                if (potentialSwaps.length > 0) {
                    this.api.reportExtraNumbers(potentialSwaps)
                        .then(() => {
                            this.storage.set<string[]>('800cltrk.extraNumbers', [
                                ...previouslyReported,
                                ...potentialSwaps,
                            ]);
                        });
                }
            });
        });

        setTimeout(() => {
            if (this.storage.get<boolean>('800cltrk.swapRemoved')) {
                return;
            }

            if (getMissingChanges()) {
                this.api.reportSwapChanged();

                this.storage.set('800cltrk.swapRemoved', true);
            }
        }, 60_000);

        setTimeout(() => {
            if (this.storage.get<boolean>('800cltrk.conflicting')) {
                return;
            }

            const scripts = document.getElementsByName('script');

            const found = cdns.reduce((carry, cdn) => {
                let found = false;

                scripts.forEach((script) => {
                    if ((script as HTMLScriptElement).src.includes(cdn)) {
                        found = true;
                    }
                });

                return carry || found;
            }, false);

            if (found) {
                this.api.reportConflictingScript();

                this.storage.set('800cltrk.conflicting', true);

                return;
            }

            // @ts-ignore
            if (window['CTMSourceEVent'] !== undefined || window['_ctm'] !== undefined) {
                this.api.reportConflictingScript();

                this.storage.set('800cltrk.conflicting', true);

                return;
            }
        }, 10_000);

        return this;
    }
}